/**
 * features/attendance/DailyAttendance.tsx
 * ------------------------------------------------------------------
 * Step 4 — Daily Attendance Entry.
 *
 * Teacher picks a class and date, then marks each student with a
 * single tap: Present / Absent / Late / Excused / Sick.
 * Defaults everyone to "present" for speed (most students are present).
 *
 * Data stored per class per date in `reportDailyAttendance`.
 * Mobile-friendly — large tap targets, swipe-friendly layout.
 *
 * Gated by `canEnterClassTeacherComment` (same teachers who do
 * attendance today via the term-total screen).
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  collection,
  where,
  orderBy,
} from 'firebase/firestore';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Search,
  Save as SaveIcon,
  Check,
  X,
  Clock,
  AlertCircle,
  CheckCircle2,
  Users,
  BarChart3,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { auditFromContext } from '@/lib/audit';
import { canEnterClassCommentFor } from '@/lib/permissions';
import { usePublicHolidays } from '@/features/hr/usePublicHolidays';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation, useAutoSave, StickySaveBar } from '@/components/ui/SaveConfirmation';
import StudentAvatar from '@/components/ui/StudentAvatar';
import {
  GradientHero,
  GlassPill,
  SoftCard,
  StatChip,
  glassFieldClass,
} from '@/components/ui/glass';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  STATUS_SHORT,
  computeDaySnapshots,
} from '@/lib/attendanceCalc';
import type { AttendanceStatus, DailyAttendanceDoc } from '@/types';

/* ==================================================================
   CONSTANTS
   ================================================================== */

const STATUSES: AttendanceStatus[] = ['present', 'absent', 'late', 'excused', 'sick'];

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function isWeekend(dateStr: string): boolean {
  const d = new Date(dateStr + 'T12:00:00');
  return d.getDay() === 0 || d.getDay() === 6;
}

/* ==================================================================
   COMPONENT
   ================================================================== */

export default function DailyAttendance() {
  const { activeStudents: students, classes, teachers } = useData();
  const { userData } = useAuth();

  const [classId, setClassId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [entries, setEntries] = useState<Record<string, AttendanceStatus>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showReasons, setShowReasons] = useState<string | null>(null);
  const [mode, setMode] = useState<'entry' | 'history'>('entry');
  const [historyDocs, setHistoryDocs] = useState<DailyAttendanceDoc[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Allowed classes
  const myClasses = useMemo(
    () =>
      classes.filter((c) =>
        canEnterClassCommentFor(userData, c.id, classes, teachers),
      ),
    [classes, userData, teachers],
  );

  // If the user can only enter attendance for ONE class (e.g. a class
  // teacher), pick it automatically — no point making them choose from a
  // one-item list. Roles that oversee several classes still choose.
  useEffect(() => {
    if (!classId && myClasses.length === 1) setClassId(myClasses[0].id);
  }, [classId, myClasses]);

  const roster = useMemo(
    () =>
      students
        .filter((s) => s.currentClass === classId)
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, classId],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((s) => s.fullName.toLowerCase().includes(q));
  }, [roster, search]);

  const classObj = classes.find((c) => c.id === classId);
  const docId = `${classId}_${date}`;

  // Block entry on non-school days so nobody records a register by mistake:
  // weekends + public holidays (the live, admin-managed list).
  const holidays = usePublicHolidays();
  const holidayLabel = holidays.holidayName(date);
  const blocked = isWeekend(date) || !!holidayLabel;

  // Load existing record for this class+date
  const loadDay = useCallback(async () => {
    if (!classId || !date) return;
    setLoading(true);
    setMsg(null);
    try {
      const snap = await getDoc(doc(db, COLLECTIONS.dailyAttendance, docId));
      if (snap.exists()) {
        const data = snap.data() as DailyAttendanceDoc;
        setEntries(data.entries ?? {});
        setReasons(data.reasons ?? {});
      } else {
        // Default: everyone present
        const init: Record<string, AttendanceStatus> = {};
        roster.forEach((s) => (init[s.id] = 'present'));
        setEntries(init);
        setReasons({});
      }
      setDirty(false);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(false);
    }
  }, [classId, date, docId, roster]);

  useEffect(() => {
    if (classId && date) loadDay();
  }, [classId, date, loadDay]);

  // Load history when switching to history tab
  const loadHistory = useCallback(async () => {
    if (!classId) return;
    setLoadingHistory(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.dailyAttendance),
          where('classId', '==', classId),
          orderBy('date', 'desc'),
        ),
      );
      setHistoryDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() } as DailyAttendanceDoc)));
    } catch (e) {
      console.error('Failed to load history:', e);
      // If index not ready, just show empty
      setHistoryDocs([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [classId]);

  useEffect(() => {
    if (mode === 'history' && classId) loadHistory();
  }, [mode, classId, loadHistory]);

  /* ---- SET STATUS ----------------------------------------------- */

  function setStatus(studentId: string, status: AttendanceStatus) {
    if (entries[studentId] === status) return; // already this status — no real change
    setEntries((prev) => ({ ...prev, [studentId]: status }));
    setDirty(true);
    touchAutoSave();
    if (status === 'present') {
      setReasons((prev) => {
        const next = { ...prev };
        delete next[studentId];
        return next;
      });
    }
  }

  function setReason(studentId: string, reason: string) {
    if ((reasons[studentId] ?? '') === reason) return;
    setReasons((prev) => ({ ...prev, [studentId]: reason }));
    setDirty(true);
  }

  function markAllPresent() {
    const init: Record<string, AttendanceStatus> = {};
    roster.forEach((s) => (init[s.id] = 'present'));
    setEntries(init);
    setReasons({});
    setDirty(true);
  }

  /* ---- SAVE ----------------------------------------------------- */

  async function handleSave() {
    if (!classId || !date) return;
    if (blocked) return; // no registers on weekends / public holidays
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving attendance…' });
    try {
      const now = new Date().toISOString();
      const payload: DailyAttendanceDoc = {
        classId,
        className: classObj?.name ?? classId,
        date,
        entries,
        reasons: Object.keys(reasons).length > 0 ? reasons : undefined as unknown as Record<string, string>,
        recordedBy: userData?.uid ?? 'system',
        recordedByName: userData?.displayName ?? 'System',
        createdAt: now,
        updatedAt: now,
      };
      if (!payload.reasons || Object.keys(payload.reasons).length === 0) {
        delete (payload as unknown as Record<string, unknown>).reasons;
      }
      await setDoc(doc(db, COLLECTIONS.dailyAttendance, docId), payload);
      setDirty(false);
      setSaveModal({ show: true, status: 'success', message: `Attendance saved for ${formatDate(date)}.` });
      auditFromContext(userData, 'attendance_saved', 'attendance', {
        targetId: classId,
        targetType: 'attendance',
        targetName: classObj?.name ?? classId,
        details: { date, studentsMarked: Object.keys(entries).length, absent: Object.values(entries).filter((v) => v === 'absent').length },
      });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  // Auto-save after 30s of inactivity when there are unsaved changes
  const { touch: touchAutoSave } = useAutoSave(
    async () => { if (dirty && classId && date) await handleSave(); },
    300000,
    dirty,
  );

  /* ---- STATS ---------------------------------------------------- */

  const stats = useMemo(() => {
    const vals = Object.values(entries);
    return {
      total: roster.length,
      present: vals.filter((v) => v === 'present').length,
      absent: vals.filter((v) => v === 'absent').length,
      late: vals.filter((v) => v === 'late').length,
      excused: vals.filter((v) => v === 'excused' || v === 'sick').length,
    };
  }, [entries, roster]);

  /* ---- RENDER --------------------------------------------------- */

  return (
    <div className="space-y-4">
      <GradientHero
        icon={Calendar}
        title="Daily Attendance"
        subtitle={classId ? `${classObj?.name ?? classId} · ${formatDate(date)}` : 'Select a class to begin'}
        actions={
          classId && mode === 'entry' && !blocked ? (
            <>
              {dirty ? (
                <GlassPill tone="amber">
                  <span className="w-2 h-2 bg-amber-300 rounded-full animate-pulse" />
                  Unsaved
                </GlassPill>
              ) : (
                <GlassPill tone="green">Saved</GlassPill>
              )}
              <button
                onClick={handleSave}
                disabled={!dirty || saving}
                className={`flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 shadow-lg ${
                  !dirty
                    ? 'bg-white/20 text-white/60 cursor-not-allowed'
                    : 'bg-white text-[#1b3a6b] hover:bg-blue-50 hover:scale-105'
                }`}
              >
                <SaveIcon size={18} />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : null
        }
      />

      {msg && (
        <div className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 ${
          msg.ok
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
            : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
        }`}>
          {msg.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
          {msg.text}
        </div>
      )}

      {blocked && mode === 'entry' && (
        <div className="text-sm rounded-xl px-4 py-3 border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle size={15} className="shrink-0" />
          {isWeekend(date)
            ? <span>No school on {formatDate(date)} — it's a weekend. Attendance can't be recorded. Pick a school day.</span>
            : <span>{formatDate(date)} is a public holiday ({holidayLabel}). Attendance can't be recorded. Pick a school day.</span>}
        </div>
      )}

      {/* Class + date selector */}
      <SoftCard pad="p-5">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Class</label>
            {myClasses.length > 1 ? (
              <select
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
                className={glassFieldClass()}
              >
                <option value="">— select class —</option>
                {myClasses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            ) : (
              <div className={`${glassFieldClass()} flex items-center`}>
                {myClasses[0]?.name ?? 'No class assigned'}
              </div>
            )}
          </div>
          <div className="sm:w-64">
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1.5 block">Date</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDate(shiftDate(date, -1))}
                className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={glassFieldClass('text-center flex-1')}
              />
              <button
                onClick={() => setDate(shiftDate(date, 1))}
                disabled={date >= todayISO()}
                className="p-2 rounded-xl border border-gray-200 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-30"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <button
              onClick={() => setDate(todayISO())}
              className="text-xs text-[#2d5295] hover:underline mt-1"
            >
              Today
            </button>
          </div>
        </div>

        {/* Mode toggle */}
        {classId && (
          <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={() => setMode('entry')}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                mode === 'entry'
                  ? 'bg-[#1b3a6b] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <Check size={14} /> Take Attendance
            </button>
            <button
              onClick={() => setMode('history')}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                mode === 'history'
                  ? 'bg-[#1b3a6b] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
            >
              <BarChart3 size={14} /> History
            </button>
          </div>
        )}
      </SoftCard>

      {!classId && (
        <SoftCard>
          <p className="text-center text-gray-400 py-8">Select a class above to take attendance.</p>
        </SoftCard>
      )}

      {classId && loading && <Spinner label="Loading attendance…" />}

      {classId && !loading && mode === 'entry' && !blocked && renderEntry()}
      {classId && mode === 'history' && renderHistory()}
    </div>
  );

  /* ================================================================
     ENTRY VIEW
     ================================================================ */

  function renderEntry() {
    return (
      <>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatChip tone="blue" value={stats.total} label="Students" icon={Users} />
          <StatChip tone="emerald" value={stats.present} label="Present" icon={Check} />
          <StatChip tone="red" value={stats.absent} label="Absent" icon={X} />
          <StatChip tone="gold" value={stats.late} label="Late" icon={Clock} />
          <StatChip tone="purple" value={stats.excused} label="Excused / Sick" icon={AlertCircle} />
        </div>

        {/* Legend + actions */}
        <SoftCard pad="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {STATUSES.map((s) => (
                <span key={s} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${STATUS_COLORS[s]}`}>
                  {STATUS_SHORT[s]} = {STATUS_LABELS[s]}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className={glassFieldClass('pl-9 py-2 w-44')}
                />
              </div>
              <button
                onClick={markAllPresent}
                className="text-xs px-3 py-2 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 font-medium"
              >
                All present
              </button>
            </div>
          </div>
        </SoftCard>

        {/* Student list — card layout for mobile, compact for desktop */}
        <SoftCard pad="p-0">
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {filtered.map((s, i) => {
              const status = entries[s.id] ?? 'present';
              return (
                <div key={s.id}>
                  <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                    {/* Row number + avatar */}
                    <span className="w-5 text-xs text-gray-400 text-right shrink-0 mt-1">{i + 1}</span>
                    <div className="shrink-0 mt-0.5">
                      <StudentAvatar student={s} size="sm" />
                    </div>

                    {/* Name + status stacked */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{s.fullName}</p>
                        {/* Reason toggle for non-present */}
                        {status !== 'present' && (
                          <button
                            onClick={() => setShowReasons(showReasons === s.id ? null : s.id)}
                            className="text-[10px] text-gray-400 hover:text-gray-600 shrink-0 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600"
                          >
                            {reasons[s.id] ? '✎ note' : '+ note'}
                          </button>
                        )}
                      </div>
                      {/* Status buttons — full width row below name */}
                      <div className="flex items-center gap-1.5">
                        {STATUSES.map((st) => {
                          const active = status === st;
                          return (
                            <button
                              key={st}
                              onClick={() => setStatus(s.id, st)}
                              title={STATUS_LABELS[st]}
                              className={`flex-1 h-8 rounded-lg text-xs font-bold border-2 transition-all ${
                                active
                                  ? STATUS_COLORS[st] + ' border-current shadow-sm scale-[1.02]'
                                  : 'border-gray-200 text-gray-400 hover:border-gray-300 dark:border-gray-600 dark:text-gray-500'
                              }`}
                            >
                              {STATUS_SHORT[st]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Reason input (expandable) */}
                  {showReasons === s.id && status !== 'present' && (
                    <div className="px-4 pb-3 pl-16">
                      <input
                        type="text"
                        placeholder={`Reason for ${STATUS_LABELS[status].toLowerCase()}…`}
                        value={reasons[s.id] ?? ''}
                        onChange={(e) => setReason(s.id, e.target.value)}
                        className={glassFieldClass('text-sm py-2')}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-400 py-8 text-center">
                {roster.length === 0
                  ? 'No students in this class.'
                  : 'No students match your search.'}
              </p>
            )}
          </div>
        </SoftCard>

        {/* Bottom save button — easy access after scrolling */}
        {dirty && (
          <div className="sticky bottom-4 z-30">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#1b3a6b] text-white font-semibold rounded-xl shadow-2xl hover:bg-[#2d5295] active:scale-[0.98] transition-all"
            >
              <SaveIcon size={20} />
              {saving ? 'Saving…' : `Save Attendance (${stats.total} students)`}
            </button>
          </div>
        )}

        <StickySaveBar
          visible={dirty && !!classId && !!date}
          saving={saving}
          onSave={handleSave}
          message="Unsaved attendance"
        />

        {/* Full-screen save confirmation */}
        <SaveConfirmation
          show={saveModal.show}
          status={saveModal.status}
          message={saveModal.message}
          onDismiss={() => setSaveModal({ ...saveModal, show: false })}
        />
      </>
    );
  }

  /* ================================================================
     HISTORY VIEW
     ================================================================ */

  function renderHistory() {
    if (loadingHistory) {
      return <SoftCard><Spinner label="Loading history…" /></SoftCard>;
    }

    if (historyDocs.length === 0) {
      return (
        <SoftCard>
          <p className="text-center text-gray-400 py-8">
            No daily attendance records for {classObj?.name ?? 'this class'} yet.
          </p>
        </SoftCard>
      );
    }

    const snapshots = computeDaySnapshots(historyDocs);

    return (
      <div className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatChip tone="blue" value={historyDocs.length} label="Days Recorded" icon={Calendar} />
          <StatChip
            tone="emerald"
            value={
              snapshots.length > 0
                ? `${Math.round(snapshots.reduce((a, s) => a + s.present, 0) / snapshots.reduce((a, s) => a + s.total, 0) * 100)}%`
                : '—'
            }
            label="Avg Attendance"
            icon={Check}
          />
          <StatChip
            tone="red"
            value={snapshots.reduce((a, s) => a + s.absent, 0)}
            label="Total Absences"
            icon={X}
          />
          <StatChip
            tone="gold"
            value={snapshots.reduce((a, s) => a + s.late, 0)}
            label="Total Lates"
            icon={Clock}
          />
        </div>

        {/* Day-by-day list */}
        <SoftCard pad="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium text-center">Present</th>
                  <th className="px-4 py-3 font-medium text-center">Absent</th>
                  <th className="px-4 py-3 font-medium text-center">Late</th>
                  <th className="px-4 py-3 font-medium text-center">Excused/Sick</th>
                  <th className="px-4 py-3 font-medium text-center">%</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.reverse().map((snap) => {
                  const pct = snap.total > 0 ? Math.round((snap.present / snap.total) * 100) : 0;
                  return (
                    <tr key={snap.date} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/20">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{formatDate(snap.date)}</td>
                      <td className="px-4 py-3 text-center text-emerald-600">{snap.present}</td>
                      <td className="px-4 py-3 text-center text-red-600">{snap.absent}</td>
                      <td className="px-4 py-3 text-center text-amber-600">{snap.late}</td>
                      <td className="px-4 py-3 text-center text-purple-600">{snap.excused + snap.sick}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          pct >= 90 ? 'bg-emerald-50 text-emerald-700' :
                          pct >= 75 ? 'bg-amber-50 text-amber-700' :
                          'bg-red-50 text-red-700'
                        }`}>
                          {pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => { setDate(snap.date); setMode('entry'); }}
                          className="text-xs text-[#2d5295] hover:underline"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SoftCard>
      </div>
    );
  }
}
